import { Scenario } from "@reco/shared/src/constants";
import type { GridBox } from "@reco/shared/src/types/gridbox";
import { getGridboxData } from "./GridboxAPIService";
import type {
  PageRecommendationsRequest,
  PageRecommendationsResponse,
  SectionResponse,
  WidgetProps,
} from "./types/pageRecos";
import { getEnvironmentConfig } from "./utils/config";
import { NewPageType } from "./utils/pageType";
import { getCookieValue } from "./utils/storage";

interface RecommendationsUrlOptions {
  recoHost: string;
  country: string;
  language: string;
  touchPoint: string;
  pageType: string;
  pageId?: string | null;
  zone?: string;
  warehouse?: string;
}

const buildPageRecommendationsUrl = (options: RecommendationsUrlOptions): string => {
  const url = new URL(
    `/r/api/recommendations/${options.country}/${options.language}/${options.touchPoint}/page-recommendations`,
    options.recoHost,
  );

  url.searchParams.set("pageType", options.pageType);

  if (options.pageId) {
    url.searchParams.set("pageId", options.pageId);
  }
  if (options.zone) {
    url.searchParams.set("zoneId", options.zone);
  }
  if (options.warehouse) {
    url.searchParams.set("wh", options.warehouse);
  }

  return url.toString();
};

/**
 * Requests multiple page recommendations for the given payload from PRECO backend
 * @param payload The request payload
 * @returns A promise that resolves to the page recommendations response or null if the user has not given marketing consent
 */
export const getPageRecommendations = async (
  payload: PageRecommendationsRequest,
): Promise<PageRecommendationsResponse | null> => {
  const environmentConfig = getEnvironmentConfig();

  if (!environmentConfig.recoHost) {
    throw new Error("[RecommendationService] recoHost is not defined in the environment configuration");
  }

  const zone = getCookieValue("zn");
  const warehouse = getCookieValue("wh");

  // TODO: Add missing userId to the request URL
  // As per confluence: "Tbd: discussion is ongoing regarding the request from legal to make the PII info more secure"
  const requestUrl = buildPageRecommendationsUrl({
    recoHost: environmentConfig.recoHost,
    country: payload.country,
    language: payload.language,
    touchPoint: payload.touchPoint,
    pageType: payload.pageType,
    pageId: payload.pageId,
    zone,
    warehouse,
  });

  const response = await fetch(requestUrl, {
    method: "POST",
    headers: { "Content-Type": "text/plain;charset=UTF-8" },
    body: JSON.stringify({
      pageSections: payload.pageSections,
      viewedProducts: payload.viewedProducts,
    }),
  });

  if (!response.ok) {
    throw new Error(`Page recommendations request failed with status ${response.status}`);
  }

  const data: PageRecommendationsResponse = await response.json();
  return data;
};

const filterResponseSections = (sections: SectionResponse[]) => {
  return sections.filter(
    (section) =>
      section.status === "OK" &&
      section.content?.recommendationItems?.length &&
      (section.pagePosition != null || section.recoPosition != null),
  );
};

const getUniqueItemIdsFromSections = (sections: SectionResponse[]): string[] => {
  return Array.from(
    new Set(
      sections
        .flatMap((section) => section.content?.recommendationItems ?? [])
        .map((item) => item.itemId)
        .filter((itemId): itemId is string => Boolean(itemId)),
    ),
  );
};

const getGridboxMap = async (
  payload: PageRecommendationsRequest,
  uniqueItemIds: string[],
): Promise<Map<string, GridBox>> => {
  const gridboxParams = {
    countryId: payload.country,
    languageId: payload.language,
    recommendationItems: uniqueItemIds,
  };
  const gridboxData = await getGridboxData(gridboxParams);
  return new Map(gridboxData?.map((gb) => [String(gb.erpNumber), gb]));
};

const buildWidgetProps = ({
  section,
  pagePosition,
  recoPosition,
  gridboxLookup,
  pageType,
  response,
}: {
  section: SectionResponse;
  pagePosition: number | undefined;
  recoPosition: number | undefined;
  gridboxLookup: Map<string, GridBox>;
  pageType: string;
  response: PageRecommendationsResponse;
}): WidgetProps => ({
  heading: section.content?.heading || "",
  subHeading: section.content?.subheading || "",
  recommendations: (section.content?.recommendationItems ?? []).map((item) => {
    const gridbox = gridboxLookup.get(String(item.itemId));
    return {
      ...gridbox,
      itemId: item.itemId,
      list: section.trackingCode,
      relevance: item.relevance ?? 0,
    };
  }),
  creativeSlot: pagePosition,
  creativeSlotReco: recoPosition,
  scenario: Scenario.FULLY_DYNAMIC,
  brand: response.brandName || "",
  category: response.categoryPathName || "",
  categoryName: response.categoryName || "",
  showEntryPoint: pageType === NewPageType.PRODUCT_DETAIL_PAGE && section.recoPosition === 1,
  isProductsResponse: section.trackingCode.split("_").at(-1) === "products",
});

const mapPageRecommendationsResponseToWidgetProps = async (
  payload: PageRecommendationsRequest,
  response: PageRecommendationsResponse,
): Promise<WidgetProps[]> => {
  const filteredSections = filterResponseSections(response.sections);
  if (!filteredSections.length) return [];

  const uniqueItemIds = getUniqueItemIdsFromSections(filteredSections);
  if (!uniqueItemIds.length) return [];

  const gridboxByErp = await getGridboxMap(payload, uniqueItemIds);
  const requestedSectionIds = new Set(payload.pageSections.map((s) => s.sectionId));

  return filteredSections.flatMap((section) => {
    const isRequestedSection = requestedSectionIds.has(section.sectionId);
    const position = section.pagePosition ?? section.recoPosition;

    if (!isRequestedSection || position == null) return [];

    return buildWidgetProps({
      section,
      pagePosition: section.pagePosition ?? undefined,
      recoPosition: section.recoPosition ?? undefined,
      gridboxLookup: gridboxByErp,
      pageType: payload.pageType,
      response,
    });
  });
};

export const getMultipleRecommendationsForPage = async (payload: PageRecommendationsRequest) => {
  const response = await getPageRecommendations(payload);
  if (!response) return null;

  const widgetProps = await mapPageRecommendationsResponseToWidgetProps(payload, response);
  return widgetProps;
};
